import React, { useEffect, useRef, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { AlertCircle, ExternalLink, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';

import { loadMedcall2DiversionParticipantAliases } from './medcallIntakeAliases';
import {
  buildMedcallIntakeSearchVariables,
  MEDCALL_SEARCH_DIVERSION_PARTICIPANTS,
} from './medcallIntakeSearchCompatibility';
import { createMedcallLatestRequestGuard } from './medcallLatestRequestGuard';

const MEDCALL_PARTICIPANT_INTAKE_OPTIONS = gql`
  query MedcallParticipantIntakeOptions {
    medcallParticipantIntakeOptions {
      facilityOptions {
        label
        value
      }
      filterDomains
      programOptions {
        label
        requiresClinicalContacts
        requiresFacility
        value
      }
    }
  }
`;

const MEDCALL_FIND_DUPLICATE_PARTICIPANT = gql`
  query MedcallFindDuplicateParticipant($diversionParticipantId: Int!, $programKey: String!) {
    medcallFindDuplicateParticipant(diversionParticipantId: $diversionParticipantId, programKey: $programKey) {
      dob
      firstname
      id
      lastname
      program
    }
  }
`;

const MEDCALL_CREATE_PARTICIPANT = gql`
  mutation MedcallCreateParticipant(
    $diversionParticipantId: Int!
    $nurseEmail: String
    $nurseFirstname: String
    $nurseLastname: String
    $nurseTitle: String
    $optionFacilityId: Int
    $programKey: String!
    $psychiatristEmail: String
    $psychiatristFirstname: String
    $psychiatristLastname: String
    $psychiatristTitle: String
  ) {
    medcallCreateParticipant(
      diversionParticipantId: $diversionParticipantId
      nurseEmail: $nurseEmail
      nurseFirstname: $nurseFirstname
      nurseLastname: $nurseLastname
      nurseTitle: $nurseTitle
      optionFacilityId: $optionFacilityId
      programKey: $programKey
      psychiatristEmail: $psychiatristEmail
      psychiatristFirstname: $psychiatristFirstname
      psychiatristLastname: $psychiatristLastname
      psychiatristTitle: $psychiatristTitle
    ) {
      duplicateParticipant {
        dob
        firstname
        id
        lastname
        program
      }
      extra
      message
      participant {
        firstname
        id
        lastname
      }
      responseCode
      responseID
    }
  }
`;

const NURSE_TITLES = ['LVN', 'MA', 'CNA', 'RN', 'LPT', 'NP', 'EMT'];
const PSYCHIATRIST_TITLES = ['MD', 'NP', 'PA'];
const EMAIL_ADDRESS_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type IntakeOption = {
  label: string;
  value: string;
};

type IntakeProgramOption = IntakeOption & {
  requiresClinicalContacts: boolean;
  requiresFacility: boolean;
};

type DiversionParticipantSearchItem = {
  cid?: string | null;
  dob?: string | null;
  firstname?: string | null;
  fullname?: string | null;
  in_use?: boolean | null;
  lastname?: string | null;
  odrparticipant?: number | null;
  participant: number;
  provider?: string | null;
  username?: string | null;
};

type DuplicateParticipant = {
  dob?: string | null;
  firstname?: string | null;
  id: number;
  lastname?: string | null;
  program?: string | null;
};

type MedcallParticipantIntakeOptionsData = {
  medcallParticipantIntakeOptions: {
    facilityOptions: IntakeOption[];
    filterDomains: string[];
    programOptions: IntakeProgramOption[];
  };
};

type MedcallSearchDiversionParticipantsData = {
  medcallSearchDiversionParticipants: DiversionParticipantSearchItem[];
};

type MedcallFindDuplicateParticipantData = {
  medcallFindDuplicateParticipant: DuplicateParticipant | null;
};

type MedcallCreateParticipantData = {
  medcallCreateParticipant: {
    duplicateParticipant?: DuplicateParticipant | null;
    extra?: string | null;
    message?: string | null;
    participant?: {
      firstname?: string | null;
      id: number;
      lastname?: string | null;
    } | null;
    responseCode?: number | null;
    responseID?: number | null;
  };
};

type MedcallParticipantIntakeDialogProps = {
  onCreated?: (participantId: number) => Promise<void> | void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  routeBase?: string;
};

const normalizeValue = (value: unknown) => `${value || ''}`.trim();

const formatDate = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const match = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return normalizedValue || '—';
  }

  return `${match[2]}/${match[3]}/${match[1]}`;
};

const isBlockedPersonalDomain = (email: string, blockedDomains: string[]) => {
  const normalizedEmail = normalizeValue(email).toLowerCase();
  if (!normalizedEmail.includes('@')) {
    return false;
  }

  const emailDomain = normalizedEmail.split('@')[1] || '';
  return blockedDomains.includes(emailDomain);
};

const InlineMessage = ({
  action,
  children,
  tone,
}: {
  action?: React.ReactNode;
  children: React.ReactNode;
  tone: 'error' | 'warning';
}) => {
  const toneClassName = tone === 'error'
    ? 'border-[var(--semantic-error-border-soft)] bg-[var(--semantic-error-soft)] text-[var(--semantic-error)]'
    : 'border-[var(--semantic-warning-border-soft)] bg-[var(--semantic-warning-soft)] text-[var(--semantic-warning)]';

  return (
    <div className={cn('rounded-[var(--border-radius-medium)] border px-3 py-2 text-sm', toneClassName)}>
      <div className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1 space-y-2">
          <div>{children}</div>
          {action}
        </div>
      </div>
    </div>
  );
};

export const MedcallParticipantIntakeDialog = ({
  onCreated,
  onOpenChange,
  open,
  routeBase = '/tools/medcall',
}: MedcallParticipantIntakeDialogProps) => {
  const isPromotedPage = routeBase === '/tools/medcall';
  const guardSearchRequests = isPromotedPage || routeBase === '/tools/medcall2';
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const [programKey, setProgramKey] = useState('');
  const [optionFacilityId, setOptionFacilityId] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [searchResults, setSearchResults] = useState<DiversionParticipantSearchItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState<DiversionParticipantSearchItem | null>(null);
  const [duplicateParticipant, setDuplicateParticipant] = useState<DuplicateParticipant | null>(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [duplicateCheckFailed, setDuplicateCheckFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submissionInFlight = useRef(false);
  const latestDuplicateRequest = useRef(createMedcallLatestRequestGuard()).current;
  const [nurseFirstname, setNurseFirstname] = useState('');
  const [nurseLastname, setNurseLastname] = useState('');
  const [nurseTitle, setNurseTitle] = useState('');
  const [nurseEmail, setNurseEmail] = useState('');
  const [nurseConfirmEmail, setNurseConfirmEmail] = useState('');
  const [psychiatristFirstname, setPsychiatristFirstname] = useState('');
  const [psychiatristLastname, setPsychiatristLastname] = useState('');
  const [psychiatristTitle, setPsychiatristTitle] = useState('');
  const [psychiatristEmail, setPsychiatristEmail] = useState('');
  const [psychiatristConfirmEmail, setPsychiatristConfirmEmail] = useState('');

  const { data, error, loading } = useQuery<MedcallParticipantIntakeOptionsData>(MEDCALL_PARTICIPANT_INTAKE_OPTIONS, {
    fetchPolicy: 'cache-and-network',
    skip: !open,
  });
  const [createParticipant, { loading: createLoading }] = useMutation<MedcallCreateParticipantData>(MEDCALL_CREATE_PARTICIPANT);
  const latestSearchRequest = useRef(createMedcallLatestRequestGuard()).current;

  const resetDialogState = () => {
    if (guardSearchRequests) {
      latestSearchRequest.invalidate();
    }
    setProgramKey('');
    setOptionFacilityId('');
    setSearchDraft('');
    setSearchResults([]);
    setSearchLoading(false);
    setSearchError('');
    setSelectedParticipant(null);
    setDuplicateParticipant(null);
    setDuplicateLoading(false);
    if (isPromotedPage) {
      latestDuplicateRequest.invalidate();
      setDuplicateCheckFailed(false);
    }
    setValidationError('');
    setNurseFirstname('');
    setNurseLastname('');
    setNurseTitle('');
    setNurseEmail('');
    setNurseConfirmEmail('');
    setPsychiatristFirstname('');
    setPsychiatristLastname('');
    setPsychiatristTitle('');
    setPsychiatristEmail('');
    setPsychiatristConfirmEmail('');
  };

  useEffect(() => {
    if (open) {
      return;
    }

    resetDialogState();
  }, [open]);

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isPromotedPage && submissionInFlight.current) return;
    if (!nextOpen) {
      resetDialogState();
    }

    onOpenChange(nextOpen);
  };

  const programOptions = data?.medcallParticipantIntakeOptions?.programOptions || [];
  const facilityOptions = data?.medcallParticipantIntakeOptions?.facilityOptions || [];
  const filterDomains = data?.medcallParticipantIntakeOptions?.filterDomains || [];
  const selectedProgram = programOptions.find((option) => option.value === programKey) || null;
  const requiresClinicalContacts = selectedProgram?.requiresClinicalContacts === true;
  const requiresFacility = selectedProgram?.requiresFacility === true;
  const selectedParticipantIsIncomplete = Boolean(
    selectedParticipant
    && (
      !normalizeValue(selectedParticipant.firstname)
      || !normalizeValue(selectedParticipant.lastname)
      || !normalizeValue(selectedParticipant.dob)
    )
  );

  const handleProgramChange = (value: string) => {
    if (isPromotedPage && submissionInFlight.current) return;
    if (isPromotedPage) {
      latestDuplicateRequest.invalidate();
      setDuplicateLoading(false);
      setDuplicateCheckFailed(false);
    }
    if (guardSearchRequests) {
      latestSearchRequest.invalidate();
    }
    setProgramKey(value);
    setOptionFacilityId('');
    setSearchDraft('');
    setSearchResults([]);
    setSearchLoading(false);
    setSearchError('');
    setSelectedParticipant(null);
    setDuplicateParticipant(null);
    setValidationError('');
    setNurseFirstname('');
    setNurseLastname('');
    setNurseTitle('');
    setNurseEmail('');
    setNurseConfirmEmail('');
    setPsychiatristFirstname('');
    setPsychiatristLastname('');
    setPsychiatristTitle('');
    setPsychiatristEmail('');
    setPsychiatristConfirmEmail('');
  };

  const handleSearch = async () => {
    if (isPromotedPage && submissionInFlight.current) return;
    if (isPromotedPage) {
      latestDuplicateRequest.invalidate();
      setDuplicateLoading(false);
      setDuplicateCheckFailed(false);
    }
    setValidationError('');

    if (!programKey) {
      setSearchError('Choose a program before searching.');
      setSearchResults([]);
      return;
    }

    if (normalizeValue(searchDraft).length < 3) {
      setSearchError('Enter at least 3 characters to search.');
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setSearchError('');
    setSelectedParticipant(null);
    setDuplicateParticipant(null);
    const requestId = latestSearchRequest.begin();

    try {
      const result = await apolloClient.query<MedcallSearchDiversionParticipantsData>({
        fetchPolicy: 'network-only',
        query: MEDCALL_SEARCH_DIVERSION_PARTICIPANTS,
        variables: buildMedcallIntakeSearchVariables({
          programKey,
          routeBase,
          searchText: normalizeValue(searchDraft),
        }),
      });

      if (guardSearchRequests && !latestSearchRequest.isLatest(requestId)) {
        return;
      }

      if (isPromotedPage && result.errors?.length) throw new Error('Unable to search participants.');
      setSearchResults(result.data?.medcallSearchDiversionParticipants || []);
    } catch (queryError: unknown) {
      if (guardSearchRequests && !latestSearchRequest.isLatest(requestId)) {
        return;
      }

      setSearchResults([]);
      setSearchError(isPromotedPage ? 'Unable to search participants.' : (queryError as { message?: string } | null)?.message || 'Unable to search participants.');
    }

    setSearchLoading(false);
  };

  const handleParticipantSelect = async (participant: DiversionParticipantSearchItem) => {
    if (isPromotedPage && submissionInFlight.current) return;
    const requestId = latestDuplicateRequest.begin();
    if (isPromotedPage) setDuplicateCheckFailed(false);
    setSelectedParticipant(participant);
    setDuplicateParticipant(null);
    setValidationError('');

    if (!normalizeValue(participant.firstname) || !normalizeValue(participant.lastname) || !normalizeValue(participant.dob)) {
      return;
    }

    setDuplicateLoading(true);

    try {
      const result = await apolloClient.query<MedcallFindDuplicateParticipantData>({
        fetchPolicy: 'network-only',
        query: MEDCALL_FIND_DUPLICATE_PARTICIPANT,
        variables: {
          diversionParticipantId: participant.participant,
          programKey,
        },
      });

      if (isPromotedPage && !latestDuplicateRequest.isLatest(requestId)) return;
      if (isPromotedPage && (result.errors?.length || !result.data || !('medcallFindDuplicateParticipant' in result.data))) {
        throw new Error('Unable to check for an existing client.');
      }
      setDuplicateParticipant(result.data?.medcallFindDuplicateParticipant || null);
    } catch (queryError: unknown) {
      if (isPromotedPage && !latestDuplicateRequest.isLatest(requestId)) return;
      setDuplicateParticipant(null);
      if (isPromotedPage) setDuplicateCheckFailed(true);
      setValidationError(isPromotedPage ? 'Unable to check for an existing client.' : (queryError as { message?: string } | null)?.message || 'Unable to check for an existing client.');
    }

    setDuplicateLoading(false);
  };

  const validateForm = () => {
    if (!programKey) {
      return 'Program is required.';
    }

    if (!selectedParticipant) {
      return 'Select a participant to continue.';
    }

    if (selectedParticipantIsIncomplete) {
      return 'The selected participant is missing a first name, last name, or date of birth.';
    }

    if (duplicateParticipant) {
      return 'This client already exists in MedCall.';
    }

    if (requiresFacility && !optionFacilityId) {
      return 'Facility is required.';
    }

    if (!requiresClinicalContacts) {
      return '';
    }

    if (!normalizeValue(nurseFirstname) || !normalizeValue(nurseLastname) || !normalizeValue(nurseTitle) || !normalizeValue(nurseEmail)) {
      return 'Nurse first name, last name, title, and email are required.';
    }

    if (!EMAIL_ADDRESS_REGEX.test(normalizeValue(nurseEmail))) {
      return 'Enter a valid nurse email address.';
    }

    if (isBlockedPersonalDomain(nurseEmail, filterDomains)) {
      return 'Use an agency email address for the nurse contact.';
    }

    if (normalizeValue(nurseEmail) !== normalizeValue(nurseConfirmEmail)) {
      return 'The nurse email addresses do not match.';
    }

    if (normalizeValue(psychiatristEmail) && !EMAIL_ADDRESS_REGEX.test(normalizeValue(psychiatristEmail))) {
      return 'Enter a valid psychiatrist email address.';
    }

    if (normalizeValue(psychiatristEmail) && isBlockedPersonalDomain(psychiatristEmail, filterDomains)) {
      return 'Use an agency email address for the psychiatrist contact.';
    }

    if (normalizeValue(psychiatristConfirmEmail) && normalizeValue(psychiatristEmail) !== normalizeValue(psychiatristConfirmEmail)) {
      return 'The psychiatrist email addresses do not match.';
    }

    return '';
  };

  const handleSubmit = async () => {
    if (isPromotedPage && (submissionInFlight.current || createLoading || loading || error || duplicateLoading || duplicateCheckFailed)) return;
    const nextValidationError = validateForm();
    if (nextValidationError) {
      setValidationError(nextValidationError);
      return;
    }

    if (!selectedParticipant) {
      return;
    }

    setValidationError('');
    if (isPromotedPage) {
      submissionInFlight.current = true;
      setSubmitting(true);
    }

    try {
      if (routeBase === '/tools/medcall2') {
        // Creation reloads aliases server-side; this preserves the legacy pre-create failure boundary.
        await loadMedcall2DiversionParticipantAliases({
          apolloClient,
          diversionParticipantId: selectedParticipant.participant,
          programKey,
          routeBase,
        });
      }

      const result = await createParticipant({
        variables: {
          diversionParticipantId: selectedParticipant.participant,
          nurseEmail: requiresClinicalContacts ? normalizeValue(nurseEmail).toLowerCase() : null,
          nurseFirstname: requiresClinicalContacts ? normalizeValue(nurseFirstname) : null,
          nurseLastname: requiresClinicalContacts ? normalizeValue(nurseLastname) : null,
          nurseTitle: requiresClinicalContacts ? normalizeValue(nurseTitle) : null,
          optionFacilityId: requiresFacility ? Number(optionFacilityId) : null,
          programKey,
          psychiatristEmail: requiresClinicalContacts && normalizeValue(psychiatristEmail)
            ? normalizeValue(psychiatristEmail).toLowerCase()
            : null,
          psychiatristFirstname: requiresClinicalContacts ? normalizeValue(psychiatristFirstname) || null : null,
          psychiatristLastname: requiresClinicalContacts ? normalizeValue(psychiatristLastname) || null : null,
          psychiatristTitle: requiresClinicalContacts ? normalizeValue(psychiatristTitle) || null : null,
        },
      });

      const response = result.data?.medcallCreateParticipant;
      if ((!isPromotedPage || !result.errors?.length) && response?.responseCode === 409 && response.duplicateParticipant) {
        setDuplicateParticipant(response.duplicateParticipant);
        setValidationError(isPromotedPage ? 'This client already exists in MedCall.' : response.message || 'This client already exists in MedCall.');
        return;
      }

      const confirmedPageCreation = !result.errors?.length && Number.isInteger(response?.participant?.id)
        && Number(response?.participant?.id) > 0 && response?.responseID === response?.participant?.id;
      if (response?.responseCode !== 200 || !response.participant?.id || (isPromotedPage && !confirmedPageCreation)) {
        setValidationError(isPromotedPage ? 'Unable to add client.' : response?.message || 'Unable to add client.');
        return;
      }

      if (onCreated) {
        try {
          await onCreated(response.participant.id);
        } catch (refreshError: unknown) {
          if (!isPromotedPage) throw refreshError;
          toast.error('The client was created, but the client list could not be refreshed. Reopen the list to see the update.');
        }
      }

      toast.success(response.message || 'Client added');
      if (isPromotedPage) {
        resetDialogState();
        onOpenChange(false);
      }
      if (!isPromotedPage) handleDialogOpenChange(false);
      navigate(`${routeBase}/participants/${response.participant.id}`, {
        state: {
          from: `${routeBase}/all-clients`,
        },
      });
    } catch (mutationError: unknown) {
      setValidationError(isPromotedPage ? 'Unable to add client.' : (mutationError as { message?: string } | null)?.message || 'Unable to add client.');
    } finally {
      if (isPromotedPage) {
        submissionInFlight.current = false;
        setSubmitting(false);
      }
    }
  };

  return (
    <Dialog onOpenChange={handleDialogOpenChange} open={open}>
      <DialogContent className="overflow-hidden p-0" contentHeight="85vh" contentMaxWidth="95vw" contentWidth="960px">
        <div className="flex h-full flex-col">
          <DialogHeader className="border-b border-[var(--neutral-gray-30)] px-4 py-4 pr-10">
            <DialogTitle>Add Client</DialogTitle>
            <DialogDescription>
              Search diversion participants, review the match, and add the client to MedCall.
            </DialogDescription>
          </DialogHeader>

          <div className="grid min-h-0 flex-1 gap-0 lg:grid-cols-[320px_minmax(0,1fr)]">
            <div className="min-h-0 space-y-4 overflow-y-auto border-b border-[var(--neutral-gray-30)] px-4 py-4 lg:border-b-0 lg:border-r">
              <div className="space-y-2">
                <Label htmlFor="medcall-intake-program">Program</Label>
                <Select disabled={isPromotedPage && submitting} onValueChange={handleProgramChange} value={programKey}>
                  <SelectTrigger id="medcall-intake-program">
                    <SelectValue placeholder={loading ? 'Loading programs' : 'Select program'} />
                  </SelectTrigger>
                  <SelectContent>
                    {programOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {requiresFacility ? (
                <div className="space-y-2">
                  <Label htmlFor="medcall-intake-facility">Facility</Label>
                  <Select disabled={isPromotedPage && submitting} onValueChange={setOptionFacilityId} value={optionFacilityId}>
                    <SelectTrigger id="medcall-intake-facility">
                      <SelectValue placeholder="Select facility" />
                    </SelectTrigger>
                    <SelectContent>
                      {facilityOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="medcall-intake-search">Search Participant</Label>
                <div className="flex items-center gap-2">
                  <Input
                    disabled={isPromotedPage && submitting}
                    id="medcall-intake-search"
                    onChange={(event) => setSearchDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter') {
                        return;
                      }

                      void handleSearch();
                    }}
                    placeholder="First name, last name, booking, CID"
                    value={searchDraft}
                  />
                  <Button disabled={isPromotedPage && submitting} className="shrink-0" onClick={() => void handleSearch()} size="sm" type="button" variant="outline">
                    <Search className="h-4 w-4" />
                    Search
                  </Button>
                </div>
                <p className="text-xs text-[var(--neutral-gray-80)]">Enter at least 3 characters.</p>
              </div>

              {searchError ? (
                <InlineMessage tone="error">
                  {searchError}
                </InlineMessage>
              ) : null}

              <div className="space-y-2">
                <div className="text-sm font-medium text-[var(--neutral-gray-120)]">Matches</div>
                <div className="rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-30)]">
                  {searchLoading ? (
                    <div className="px-3 py-4 text-sm text-[var(--neutral-gray-80)]">Searching…</div>
                  ) : null}

                  {!searchLoading && searchResults.length < 1 ? (
                    <div className="px-3 py-4 text-sm text-[var(--neutral-gray-80)]">Search to load matches.</div>
                  ) : null}

                  {!searchLoading ? (
                    <div className="max-h-[420px] overflow-y-auto">
                      {searchResults.map((participant) => {
                        const isSelected = participant.participant === selectedParticipant?.participant;
                        const fullName = normalizeValue(participant.fullname) || `${normalizeValue(participant.firstname)} ${normalizeValue(participant.lastname)}`.trim();

                        return (
                          <button
                            disabled={isPromotedPage && submitting}
                            key={`${participant.participant}-${participant.odrparticipant}`}
                            className={cn(
                              'flex w-full flex-col items-start gap-1 border-b border-[var(--neutral-gray-20)] px-3 py-3 text-left transition-colors last:border-b-0 hover:bg-[var(--surface-muted)]',
                              isSelected ? 'bg-[var(--azure-soft)]' : 'bg-transparent'
                            )}
                            onClick={() => void handleParticipantSelect(participant)}
                            type="button"
                          >
                            <div className="flex w-full items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-[var(--neutral-gray-120)]">{fullName || 'Unnamed participant'}</div>
                                <div className="text-xs text-[var(--neutral-gray-80)]">
                                  DOB {formatDate(participant.dob)}{participant.cid ? ` • CID ${participant.cid}` : ''}
                                </div>
                              </div>
                              <span className="text-xs font-medium text-[var(--azure-primary)]">Select</span>
                            </div>
                            {participant.in_use ? (
                              <div className="text-xs text-[var(--semantic-warning)]">
                                Already added{participant.username ? ` by ${participant.username}` : ''}
                              </div>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="min-h-0 space-y-4 overflow-y-auto px-4 py-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Selected Participant</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {selectedParticipant ? (
                    <>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div>
                          <div className="text-xs uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">Name</div>
                          <div className="text-[var(--neutral-gray-120)]">
                            {normalizeValue(selectedParticipant.fullname) || `${normalizeValue(selectedParticipant.firstname)} ${normalizeValue(selectedParticipant.lastname)}`.trim() || '—'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">DOB</div>
                          <div className="text-[var(--neutral-gray-120)]">{formatDate(selectedParticipant.dob)}</div>
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">Participant ID</div>
                          <div className="text-[var(--neutral-gray-120)]">{selectedParticipant.participant || '—'}</div>
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">CID</div>
                          <div className="text-[var(--neutral-gray-120)]">{normalizeValue(selectedParticipant.cid) || '—'}</div>
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">Provider</div>
                          <div className="text-[var(--neutral-gray-120)]">{normalizeValue(selectedParticipant.provider) || '—'}</div>
                        </div>
                        <div>
                          <div className="text-xs uppercase tracking-[0.08em] text-[var(--neutral-gray-80)]">ODR Participant</div>
                          <div className="text-[var(--neutral-gray-120)]">{selectedParticipant.odrparticipant || '—'}</div>
                        </div>
                      </div>
                      {duplicateLoading ? (
                        <div className="text-xs text-[var(--neutral-gray-80)]">Checking for an existing MedCall client…</div>
                      ) : null}
                    </>
                  ) : (
                    <div className="text-[var(--neutral-gray-80)]">No participant selected yet.</div>
                  )}
                </CardContent>
              </Card>

              {selectedParticipantIsIncomplete ? (
                <InlineMessage tone="warning">
                  The selected participant is missing a first name, last name, or date of birth and cannot be added yet.
                </InlineMessage>
              ) : null}

              {duplicateParticipant ? (
                <InlineMessage
                  action={(
                    <Button
                      className="h-auto p-0 text-sm text-[var(--azure-primary)]"
                      onClick={() => {
                        handleDialogOpenChange(false);
                        navigate(`${routeBase}/participants/${duplicateParticipant.id}`, {
                          state: {
                            from: `${routeBase}/all-clients`,
                          },
                        });
                      }}
                      type="button"
                      variant="link"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open Existing Client
                    </Button>
                  )}
                  tone="warning"
                >
                  {normalizeValue(duplicateParticipant.firstname)} {normalizeValue(duplicateParticipant.lastname)} already exists in MedCall
                  {duplicateParticipant.program ? ` (${duplicateParticipant.program})` : ''}.
                </InlineMessage>
              ) : null}

              {validationError ? (
                <InlineMessage tone="error">
                  {validationError}
                </InlineMessage>
              ) : null}

              {error ? (
                <InlineMessage tone="error">
                  {isPromotedPage ? 'Unable to load intake options. Try again.' : error.message}
                </InlineMessage>
              ) : null}

              {requiresClinicalContacts ? (
                <>
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Nurse Contact</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="medcall-intake-nurse-firstname">First Name</Label>
                        <Input disabled={isPromotedPage && submitting} id="medcall-intake-nurse-firstname" onChange={(event) => setNurseFirstname(event.target.value)} value={nurseFirstname} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="medcall-intake-nurse-lastname">Last Name</Label>
                        <Input disabled={isPromotedPage && submitting} id="medcall-intake-nurse-lastname" onChange={(event) => setNurseLastname(event.target.value)} value={nurseLastname} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="medcall-intake-nurse-title">Title</Label>
                        <Select disabled={isPromotedPage && submitting} onValueChange={setNurseTitle} value={nurseTitle}>
                          <SelectTrigger id="medcall-intake-nurse-title">
                            <SelectValue placeholder="Select title" />
                          </SelectTrigger>
                          <SelectContent>
                            {NURSE_TITLES.map((title) => (
                              <SelectItem key={title} value={title}>
                                {title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="medcall-intake-nurse-email">Email</Label>
                        <Input disabled={isPromotedPage && submitting} id="medcall-intake-nurse-email" onChange={(event) => setNurseEmail(event.target.value)} type="email" value={nurseEmail} />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="medcall-intake-nurse-confirm-email">Retype Email</Label>
                        <Input disabled={isPromotedPage && submitting} id="medcall-intake-nurse-confirm-email" onChange={(event) => setNurseConfirmEmail(event.target.value)} type="email" value={nurseConfirmEmail} />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Psychiatrist</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="medcall-intake-psychiatrist-firstname">First Name</Label>
                        <Input disabled={isPromotedPage && submitting} id="medcall-intake-psychiatrist-firstname" onChange={(event) => setPsychiatristFirstname(event.target.value)} value={psychiatristFirstname} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="medcall-intake-psychiatrist-lastname">Last Name</Label>
                        <Input disabled={isPromotedPage && submitting} id="medcall-intake-psychiatrist-lastname" onChange={(event) => setPsychiatristLastname(event.target.value)} value={psychiatristLastname} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="medcall-intake-psychiatrist-title">Title</Label>
                        <Select disabled={isPromotedPage && submitting} onValueChange={setPsychiatristTitle} value={psychiatristTitle}>
                          <SelectTrigger id="medcall-intake-psychiatrist-title">
                            <SelectValue placeholder="Select title" />
                          </SelectTrigger>
                          <SelectContent>
                            {PSYCHIATRIST_TITLES.map((title) => (
                              <SelectItem key={title} value={title}>
                                {title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="medcall-intake-psychiatrist-email">Email</Label>
                        <Input disabled={isPromotedPage && submitting} id="medcall-intake-psychiatrist-email" onChange={(event) => setPsychiatristEmail(event.target.value)} type="email" value={psychiatristEmail} />
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="medcall-intake-psychiatrist-confirm-email">Retype Email</Label>
                        <Input disabled={isPromotedPage && submitting} id="medcall-intake-psychiatrist-confirm-email" onChange={(event) => setPsychiatristConfirmEmail(event.target.value)} type="email" value={psychiatristConfirmEmail} />
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Clinical Contacts</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-[var(--neutral-gray-80)]">
                    This program does not require facility, nurse, or psychiatrist intake fields.
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          <DialogFooter className="border-t border-[var(--neutral-gray-30)] px-4 py-4">
            <Button disabled={isPromotedPage && submitting} onClick={() => handleDialogOpenChange(false)} type="button" variant="outline">
              Cancel
            </Button>
            <Button disabled={createLoading || loading || duplicateLoading || (isPromotedPage && (submitting || duplicateCheckFailed || Boolean(error)))} onClick={() => void handleSubmit()} type="button">
              Add Client
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
